/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { graphConfig, protectedResources } from '../authConfig'
import { msalInstance } from '../index'

import { Client } from '@microsoft/microsoft-graph-client'

const getToken = async () => {
  const account = msalInstance.getActiveAccount()

  if (!account) {
    throw Error('No active account! Verify a user has been signed in and setActiveAccount has been called.')
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      account: account,
      scopes: protectedResources.apiPortal.scopes,
    })
    return response.accessToken
  } catch (error) {
    // Handle token acquisition errors
    console.error('Token acquisition error: ', error)
    msalInstance.loginRedirect()
  }
}

const getTokenAdmin = async () => {
  const account = msalInstance.getActiveAccount()

  if (!account) {
    throw Error('No active account! Verify a user has been signed in and setActiveAccount has been called.')
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      account: account,
      scopes: graphConfig.scopes,
    })
    return response.accessToken
  } catch (error) {
    // Handle token acquisition errors
    console.error('Token acquisition error: ', error)
    msalInstance.loginRedirect()
  }
}

// Absence
export const getAbsences = async (queryParams) => {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)

  const options = {
    method: 'GET',
    headers: headers,
  }

  const url = new URL(protectedResources.apiPortal.absenceEndpoint)
  url.search = new URLSearchParams(queryParams).toString()

  return fetch(url, options)
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          // Unauthorized, trigger login
          await msalInstance.loginRedirect()
          return
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .catch((error) => {
      console.error('Fetch error: ', error)
      return { content: [], totalElements: 0 }
    })
}

export const getClientiCommesse = async (email) => {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)
  const options = {
    method: 'GET',
    headers: headers,
  }
  const query = '?mail=' + encodeURIComponent(email)
  return fetch(protectedResources.apiPortal.clientiCommesseEndpoint + `${query}`, options)
    .then((response) => response.json())
    .catch((error) => {
      console.log(error)
      return []
    })
}

export const postAbsence = async (absence) => {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)
  headers.append('Content-Type', 'application/json')

  const options = {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(absence),
  }

  console.log('postAbsence: ', absence)
  return fetch(protectedResources.apiPortal.absenceEndpoint, options)
    .then((response) => response.json())
    .catch((error) => console.log(error))
}

// Admin

// Absence
export const getAdminAbsences = async (queryParams) => {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)

  const options = {
    method: 'GET',
    headers: headers,
  }

  const url = new URL(protectedResources.apiPortal.adminAbsenceEndpoint)
  if (queryParams) {
    url.search = new URLSearchParams(queryParams).toString()
  }

  return fetch(url, options)
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          // Unauthorized, trigger login
          await msalInstance.loginRedirect()
          return
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .catch((error) => {
      console.error('Fetch error: ', error)
      return []
    })
}

export const approveRejectAbsence = async (id, task) => {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)
  headers.append('Content-Type', 'application/json')

  const options = {
    method: 'PUT',
    headers: headers,
    body: JSON.stringify(task),
  }

  return fetch(protectedResources.apiPortal.adminAbsenceEndpoint + `/${id}`, options)
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          // Unauthorized, trigger login
          await msalInstance.loginRedirect()
          return
        }
        if (response.status === 403) {
          console.log('Non autorizzato')
          // TODO: Mostrare pop-up non autorizzato
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .catch((error) => {
      console.error('Fetch error: ', error)
      return null // Return null or handle the error appropriately
    })
}

export const getWindowAbsences = async ({ date, startDate, endDate, page, size }) => {
  const accessToken = await getToken()

  const headers = new Headers()
  const bearer = `Bearer ${accessToken}`

  headers.append('Authorization', bearer)
  const options = {
    method: 'GET',
    headers: headers,
  }

  let query = `?page=${page}&size=${size}`
  if (date) {
    query += `&date=${date}`
  }
  if (startDate && endDate) {
    query += `&startDate=${startDate}&endDate=${endDate}`
  }

  return fetch(protectedResources.apiPortal.absencesMonthWindowEndpoint + query, options)
    .then((response) => response.json())
    .catch((error) => {
      console.log(error)
      return []
    })
}

export async function getUsers() {
  let users = []
  try {
    const accessToken = await getTokenAdmin()
    let client = Client.init({
      authProvider: (done) => {
        done(null, accessToken)
      },
    })

    let response = await client.api('/users').select('id,displayName,userPrincipalName,mail').get()

    users = users.concat(response.value)

    while (response['@odata.nextLink']) {
      response = await client.api(response['@odata.nextLink']).get()
      users = users.concat(response.value)
    }
  } catch (error) {
    console.error('Failed to fetch users from Microsoft Graph API', error)
    throw error
  }
  return users
}
